import commonConstants from "$/data/common";
import { useAuth } from "$/util/auth";
import { gradientGrainStyling } from "$/util/background";
import { pricing } from "$/util/prices";
import { Check, Close, ThumbUp } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  RadioGroup,
  Stack,
  Typography,
  emphasize,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import analytics from "../util/analytics";
import Section from "./Section";
import SectionHeader from "./SectionHeader";

function PricingSection(props) {
  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const plans = [
    {
      id: "free",
      name: "Free",
      perks: [
        `${commonConstants.FREE_CREDITS} credits* to try out the product`,
        "Access to the style and tattoo ideas directory",
      ],
      negative_perks: [
        "Limited trial of the AI tattoo generator",
        "Only generic templates available",
        "Common styles only (blackwork, realism, etc)",
        "No control over tattoo complexity, colors, etc",
        "No access to super resolution downloads",
        "Some images are locked & blurred",
        "All designs you create are public",
        "Designs may be deleted after 30 days",
      ],
      action: "Join for Free",
    },
    {
      id: "pro",
      name: (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-start"
        >
          <img src="/images/pro_badge.svg" alt="Pro Badge" height={40} />
          <Box>Pro Unlimited</Box>
        </Stack>
      ),
      perks: [
        "Create unlimited tattoo designs",
        "Unlimited access to all available tools",
        "Templates by placement (sleeves, body parts, etc)",
        "Common & Special styles (ignorant, chicano, etc)",
        "Control tattoo complexity, colors, line weight, etc",
        "Unlimited 4x super resolution image downloads",
        "Unlimited image-to-stencil converter usage",
        "Access to the style and tattoo ideas directory",
        "All images are unlocked and unblurred",
        "Ability to keep your designs private",
        "Keep your designs forever",
      ],
    },
  ];

  const [selectedPlanId, setSelectedPlanId] = React.useState(pricing[0]);
  const handlePlanChange = (event: {
    id: string;
    freq: string;
    save: string;
    day_price: string;
    price: string;
    price_postfix: string;
    price_postfix_sub: string;
    action: string;
  }) => {
    setSelectedPlanId(event);
  };
  return (
    <Section
      bgColor={props.bgColor}
      size={props.size}
      bgImage={props.bgImage}
      bgImageOpacity={props.bgImageOpacity}
    >
      <Container>
        <SectionHeader
          title={props.title}
          subtitle={props.subtitle}
          size={3}
          subheadingSize={5}
          seoSize={1}
          textAlign="center"
        />
        <Grid container spacing={2}>
          {plans.map((plan, index) => (
            <Grid
              item
              xs={12}
              sm={plan.id === "free" ? 5 : 7}
              md={plan.id === "free" ? 3 : 4}
              key={index}
              order={{ xs: plan.id === "free" ? 3 : 2, md: index + 1 }}
            >
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    padding: theme.spacing(3),
                    backgroundColor:
                      plan.id === "pro"
                        ? theme.palette.primary.main
                        : theme.palette.background.default,
                    color:
                      plan.id === "pro"
                        ? theme.palette.primary.contrastText
                        : "inherit",
                    ...(plan.id === "pro" && gradientGrainStyling),
                  }}
                >
                  <Typography variant="h4" component="h2">
                    {plan.name}
                  </Typography>
                  {plan.perks && (
                    <Box mt={1}>
                      <List aria-label="perks">
                        {plan.perks.map((perk, idx) => (
                          <ListItem disableGutters key={idx}>
                            <ListItemIcon
                              sx={{
                                color: `${emphasize(
                                  theme.palette.background.default,
                                  0.25,
                                )} !important`,
                                minWidth: "36px !important",
                              }}
                            >
                              <Check />
                            </ListItemIcon>
                            <ListItemText>{perk}</ListItemText>
                          </ListItem>
                        ))}
                        {plan.negative_perks &&
                          plan.negative_perks.map((perk, idx) => (
                            <ListItem disableGutters key={idx}>
                              <ListItemIcon
                                sx={{
                                  color: `${emphasize(
                                    theme.palette.background.default,
                                    0.25,
                                  )} !important`,
                                  minWidth: "36px !important",
                                }}
                              >
                                <Close color="error" />
                              </ListItemIcon>
                              <ListItemText>{perk}</ListItemText>
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  )}

                  {!auth.user && plan.action && (
                    <Box mt="auto" pt={3}>
                      <Button
                        component={Link}
                        to={`/login?next=/purchase/${plan.id}`}
                        onClick={() => {
                          analytics.track("cta-click", {
                            context: "pricing",
                            action: "join",
                            button_text: "join_free",
                          });
                        }}
                        variant={plan.id === "pro" ? "contained" : "outlined"}
                        color={plan.id === "pro" ? "primary" : "inherit"}
                        size="medium"
                        fullWidth
                      >
                        {plan.action}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} md={5} order={{ xs: 1, md: 3 }}>
            {pricing.map((price, index) => (
              <Card
                onClick={() => handlePlanChange(price)}
                variant="outlined"
                sx={{
                  justifyContent: "center",
                  marginBottom: theme.spacing(2),
                  border:
                    price.id === selectedPlanId.id
                      ? `2px solid ${theme.palette.primary.main}`
                      : `2px solid ${theme.palette.divider}`,
                  cursor: "pointer",
                }}
                key={index}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    padding: theme.spacing(2),
                    color:
                      selectedPlanId.id === price.id
                        ? theme.palette.primary.main
                        : "inherit",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    key={index}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack sx={{ textAlign: "center" }}>
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        value={selectedPlanId?.id}
                        name="radio-buttons-group"
                        onChange={() => handlePlanChange(price)}
                      >
                        <FormControlLabel
                          value={price.id}
                          control={
                            <Radio
                              sx={{
                                "&.Mui-checked": { color: theme.palette.primary.main },
                              }}
                            />
                          }
                          label={
                            <>
                              <Typography variant="h5" component="p" align="left">
                                {price.freq}
                              </Typography>
                              <span style={{ float: "left" }}>
                                ${price.day_price}/day
                              </span>

                              {price.save && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label={price.save}
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </>
                          }
                        />
                      </RadioGroup>
                    </Stack>
                    <Stack>
                      <Typography variant="h6" component="p">
                        ${price.price} {price.price_postfix}
                      </Typography>

                      {price.price_postfix_sub && (
                        <Typography variant="caption">
                          {price.price_postfix_sub}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button
              fullWidth
              variant={"contained"}
              onClick={() => {
                analytics.track("cta-click", {
                  context: "pricing",
                  action: "subscribe",
                  button_text: "subscribe",
                });

                /**
                 * if authed show purchase directly
                 * else force login
                 */
                if (!auth.isAnonymousUser) {
                  navigate(
                    selectedPlanId.price
                      ? `/purchase/${selectedPlanId.id}`
                      : "/collection",
                  );
                } else {
                  navigate(
                    selectedPlanId.price
                      ? `/login?next=/purchase/${selectedPlanId.id}`
                      : "/login",
                  );
                }
              }}
              // variant={price.id === "pro_yearly" ? "contained" : "outlined"}
              color={"primary"}
              sx={{
                ...gradientGrainStyling,
              }}
              size="medium"
            >
              Continue
              {/*{price.action}*/}
            </Button>
            <Stack direction="row" spacing={2} alignItems="center" my={2}>
              <ThumbUp />
              <Stack>
                <Typography>100% Money Back Guarantee</Typography>
                <Typography variant="body2">
                  Try Pro Unlimited up to 25 generations. If you&apos;re not happy, let
                  us know your feedback and we&apos;ll refund you.
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="caption">
              All prices in USD. Cancel your subscription at any time.
            </Typography>
          </Grid>
          <Grid item xs={12} order={{ xs: 4 }}>
            <Typography variant="caption" sx={{ mt: 2 }}>
              * 1 Run of the Tattoo Designer = 2-4 Tattoo Images = 1 Credit
              <br />1 Personal Image Upload / Tattooify = 1 Credit
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
}

export default PricingSection;
